import { Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import Swal from "sweetalert2";

import env from '../env/src_config';
import LayoutRoutes from './LayoutRoutes';
import Loader from '../layout/loader/Loader';
import { isNull, parseJwt } from '../izUtils';
import { headersState, tokenState, wsIdState, wsNewMessageState, wsSubscribeToState } from '../recoil/recoil';
import SignIn from '../auth/Signin';
// import SignUp from '../auth/SignUp';
import Verify from '../auth/Verify';
import PasswordResset from '../auth/PasswordResset';
import TaskApprove from '../auth/TaskApprove';
import Spinner from '../components/spinner/Spinner';
import { startAndroidTracking, stopAndroidTracking } from '../helpers/androidInterface';

const Routers = () => {
    const { t } = useTranslation();
    const [cookies, setCookie, removeCookie] = useCookies(['token']);

    const [headers, setHeaders] = useRecoilState(headersState);
    const [tokenData, setTokenData] = useRecoilState(tokenState);
    const setWebSocketId = useSetRecoilState(wsIdState);
    const setWebSocketNewMessage = useSetRecoilState(wsNewMessageState);
    const webSocketSubscribeTo = useRecoilValue(wsSubscribeToState);

    const ws = useRef(null)

    useEffect(() => {
        let _headers = {...headers};
        let _tokenData = {...tokenData};
        let kuki = cookies;
        if (!isNull(kuki) && !isNull(kuki.token)) {
            const parsedToken = parseJwt(kuki.token);
            if (parsedToken.exp * 1000 < Date.now()) {
                Swal.fire({
                    title: t('logout.title'),
                    html: t('logout.text'),
                    icon: 'error',
                    confirmButtonColor: 'var(--theme-default)',
                    confirmButtonText: t('ok')
                });
                stopAndroidTracking('expired');
                removeCookie('token', { path: '/' })
            } else {
                _headers["Authorization"] = 'Bearer ' + kuki.token;
                setHeaders(_headers);
                _tokenData.token = kuki.token;
                _tokenData.parsedToken = parsedToken;
                setTokenData(_tokenData);
            }
        } else {
            delete _headers["Authorization"];
            setHeaders(_headers);
            setTokenData({token: "", parsedToken: null});
            localStorage.clear();
            stopAndroidTracking('missing');
            closeWebSocket()
        }
    }, [cookies]) // eslint-disable-line react-hooks/exhaustive-deps

    // Make connection to WS
    useEffect(() => {
        if (!isNull(tokenData.token) && tokenData.token.length > 0) {
            ws.current = new WebSocket(env.wss+tokenData.token);

            ws.current.onopen = () => {
                console.log('Connected to WebSocket');
            };

            ws.current.onmessage = (event) => {
                const json = JSON.parse(event.data);
                console.log('WebSocket message received:', json);

                try {
                    if (json.event === "pusher:connection_established") {
                        const data = JSON.parse(json.data);
                        setWebSocketId(data.socket_id);
                    }

                    if (json.event === "task.commented") {
                        setWebSocketNewMessage(JSON.parse(json.data));
                    }
                } catch (err) {
                    console.log(err);
                }
            };

            ws.current.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            ws.current.onclose = () => {
                console.log('WebSocket connection closed');
            };
        }

        return () => {
            closeWebSocket();
        }
    }, [tokenData]) // eslint-disable-line react-hooks/exhaustive-deps

    // Subscribe to WS channel
    useEffect(() => {
        if (!isNull(ws.current) && !isNull(webSocketSubscribeTo)) {
            ws.current.send(JSON.stringify({
                event: "pusher:subscribe",
                data: webSocketSubscribeTo
            }));
        }
    }, [webSocketSubscribeTo])

    useEffect(() => {
        document.title = env.organization_name + " - desktop app";

        // Set organization colors
        const root = document.documentElement;
        root?.style.setProperty("--theme-default", env?.colors?.primary_color);
        root?.style.setProperty("--theme-default-hover", env?.colors?.primary_color_hover);
        root?.style.setProperty("--theme-secondary", env?.colors?.secondary_color);
        root?.style.setProperty("--theme-secondary-hover", env?.colors?.secondary_color_hover);
        root?.style.setProperty("--theme-header-color", env?.header_color);

        const organization = window?.location?.hostname?.split('.')[0];
        if (!isNull(organization)) {
            document.body.classList.add(organization)
        }

    }, [])

    useEffect(() => {
        let link = document.querySelector("link[rel*='icon']");

        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }

        link.href = `/favicon/${env?.organization_name.toLowerCase().replace(' ', '')}.ico`;

    }, [])

    const signIn = (token) => {
        setCookie('token', token, { path: '/' })
        startAndroidTracking(token, t);
    }
    
    window.loginWithJwt = (token) => {
        if (!token) return;

        try {
            signIn(token);
            console.log("Auto-login via JWT complete");
        } catch (err) {
            console.error("Error in loginWithJwt", err);
        }
    };

    const closeWebSocket = () => {
        if (!isNull(ws.current)) {
            ws.current.close();
            ws.current = null;
        }
    }

    if (isNull(tokenData.token)) {
        <Loader />
    } else {
        return (
            <BrowserRouter basename={'/'}>
                <Suspense fallback={<Spinner />}>
                    {tokenData.token.length === 0 ?
                        <Routes>
                            <Route exact path={"/login/:type?"} element={<SignIn signIn={signIn}/>} />
                            {/* <Route exact path={"/register"} element={<SignUp />} /> */}
                            <Route exact path={"/password-reset/:token"} element={ <PasswordResset />} />
                            <Route exact path={"/email/verify/:token"} element={<Verify />} />
                            <Route exact path={"/task/approve/:token"} element={<TaskApprove />} />
                            <Route path="*" element={<Navigate to="/login" />} />
                        </Routes>
                        :
                        <Routes>
                            <Route path={"/*"} element={<LayoutRoutes />} />
                        </Routes>
                    }
                </Suspense>
            </BrowserRouter>
        )
    }
}
export default Routers;